import { cloneElement, ElementType, FC, MouseEventHandler, ReactElement, ReactNode } from 'react'
import { Dropdown } from './Dropdown'
import { Link } from 'react-router-dom'
import { ChevronRightIcon } from '@heroicons/react/solid'
import { cn } from 'utilities/StringUtils'

interface Avatar {
  url?: string
  initials: string
}

interface Image {
  url?: string
  alt?: string
}

export interface ResourceListItemProps {
  avatar?: Avatar
  badge?: ReactNode
  classes?: Partial<{
    item: string
  }>
  className?: string
  compact?: boolean
  disabled?: boolean
  dropdownAboveModal?: boolean
  dropdownItems?: ReactElement[]
  offeringImage?: Image
  onClick?: MouseEventHandler<HTMLButtonElement>
  showDisclosureArrow?: boolean
  subtitle?: ReactNode
  title?: ReactNode
  titleLink?: string
  toggle?: ReactNode
  truncateSubtitle?: boolean
  truncateTitle?: boolean
}

export const ResourceListItem: FC<ResourceListItemProps> = ({
  toggle,
  title,
  titleLink,
  subtitle,
  truncateSubtitle = false,
  truncateTitle = false,
  badge,
  dropdownItems: inputDropdownItems = [],
  showDisclosureArrow = false,
  compact = false,
  disabled = false,
  avatar,
  offeringImage,
  dropdownAboveModal = false,
  onClick,
  classes,
  className,
}) => {
  const dropdownItems = inputDropdownItems.filter(Boolean).map((item, i) =>
    cloneElement(item, {
      key: `dropdown_item_${i}`,
    })
  )

  const interactive = Boolean(onClick && !disabled)
  const Item: ElementType = interactive ? 'button' : 'div'

  return (
    <li
      className={cn(
        'flex items-start overflow-hidden',
        {
          'hover:bg-gray-50 cursor-pointer group': showDisclosureArrow && !disabled,
          '!cursor-not-allowed opacity-50': disabled,
        },
        compact ? '[&>*]:py-3' : '[&>*]:py-4',
        className
      )}
    >
      <Item
        aria-label={typeof title === 'string' ? title : undefined}
        className={cn('px-4 relative flex grow overflow-hidden text-sm text-left', classes?.item)}
        disabled={interactive ? disabled : undefined}
        onClick={interactive ? (onClick as MouseEventHandler) : undefined}
        type={interactive ? 'button' : undefined}
      >
        <div className="flex grow items-center space-x-4">
          {avatar && (
            <div className="flex-none">
              {avatar?.url ? (
                <img className="size-10 rounded-full" src={avatar.url} alt={avatar.initials} />
              ) : (
                <div className="size-10 rounded-full bg-gray-200 p-2 flex items-center justify-center">
                  <span className="font-medium text-sm text-gray-700">{avatar.initials}</span>
                </div>
              )}
            </div>
          )}

          {offeringImage && (
            <div className="w-[100px] flex-none">
              <div className="bg-gray-300 dark:bg-gray-800 rounded-md overflow-hidden aspect-offering flex items-center relative">
                {offeringImage.url && (
                  <img
                    src={offeringImage.url}
                    alt={offeringImage.alt}
                    className="size-full aspect-offering object-center object-cover"
                  />
                )}
              </div>
            </div>
          )}

          <div className="flex flex-1 flex-col">
            <div className={cn('flex items-center space-x-1', { truncate: truncateTitle })}>
              {toggle}

              {title && (
                <div className="font-medium text-gray-800 dark:text-gray-200">
                  {!titleLink || disabled ? (
                    title
                  ) : (
                    <Link
                      className="before:content-empty before:absolute before:inset-0 before:z-10"
                      to={titleLink}
                      title={title.toString()}
                      rel="noreferrer"
                    >
                      {title}
                    </Link>
                  )}
                </div>
              )}

              {badge}
            </div>

            {subtitle && (
              <div
                className={cn('text-gray-500', compact ? 'mt-1' : 'mt-2', {
                  truncate: truncateSubtitle,
                })}
              >
                {subtitle}
              </div>
            )}
          </div>

          {showDisclosureArrow && dropdownItems.length === 0 && (
            <div className="self-center pointer-events-none">
              <ChevronRightIcon
                className="size-6 group-hover:text-gray-600 text-gray-700"
                aria-hidden
              />
            </div>
          )}
        </div>
      </Item>

      {dropdownItems.length > 0 && (
        <Dropdown
          className="flex-initial"
          classes={{ button: 'm-0' }}
          threeDots
          aboveModal={dropdownAboveModal}
        >
          {dropdownItems}
        </Dropdown>
      )}
    </li>
  )
}
